import { ObjectParamMapping } from '@superfunnel/types';
import Autocomplete from '@superfunnel/ui-core/Autocomplete';
import Button from '@superfunnel/ui-core/v2/Button';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { useMemo, useState } from 'react';
import { HubSpotProperty } from '../../services/IntegrationsService';

// SuperFunnel lead fields that can be mapped (users can also enter custom paths)
const SUPERFUNNEL_LEAD_KEYS = [
  { key: 'data.form.email.value', example: 'john@example.com' },
  { key: 'data.form.email.label', example: 'Email' },
  { key: 'data.form.name.value', example: 'John Doe' },
  { key: 'data.form.name.label', example: 'Name' },
  { key: 'data.form.phone.value', example: '+1234567890' },
  { key: 'data.form.phone.label', example: 'Phone' },
  { key: 'data.form.message.value', example: 'Hello...' },
  { key: 'data.form.message.label', example: 'Message' },
  { key: 'data.formName', example: 'Lead capture form' },
  { key: 'data.url', example: 'https://example.page/landing' },
  { key: 'data.accountId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.campaignId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.variantId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.websiteId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.formId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.id', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.userHash', example: '1000000000000000' },
  { key: 'data.sessionHash', example: '1000000000000000' },
  { key: 'data.queryParams.gclid', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.queryParams.utm_source', example: 'google' },
  { key: 'data.queryParams.utm_medium', example: 'cpc' },
  { key: 'data.queryParams.utm_campaign', example: 'summer_sale' },
];

const SUPERFUNNEL_EVENT_KEYS = [
  { key: 'type', example: 'view' },
  { key: 'data.accountId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.campaignId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.variantId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.websiteId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.url', example: 'https://example.page/landing' },
  { key: 'data.userHash', example: '1000000000000000' },
  { key: 'data.sessionHash', example: '1000000000000000' },
  { key: 'data.queryParams.gclid', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.queryParams.utm_source', example: 'google' },
  { key: 'data.queryParams.utm_medium', example: 'cpc' },
  { key: 'data.queryParams.utm_campaign', example: 'summer_sale' },
];

interface TypedTextFieldProps {
  placeholder?: string;
  value: any;
  onChange: (value: any) => void;
}

const TypedTextField = ({
  placeholder,
  value,
  onChange,
}: TypedTextFieldProps) => {
  const type = useMemo(() => {
    return typeof value === 'string'
      ? 'string'
      : typeof value === 'number'
        ? 'number'
        : typeof value === 'boolean'
          ? 'boolean'
          : 'null';
  }, [value]);

  return (
    <div className="flex w-full items-center gap-4">
      <div className={type === 'null' ? 'w-full' : 'w-32'}>
        <Select
          value={type}
          onValueChange={(value: 'string' | 'number' | 'boolean' | 'null') => {
            if (value === 'string') {
              onChange('');
            } else if (value === 'number') {
              onChange(0);
            } else if (value === 'boolean') {
              onChange(false);
            } else {
              onChange(null);
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Value Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="string">String</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
            <SelectItem value="null">Null</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {type !== 'null' && (
        <div className="flex-1">
          {type === 'string' && (
            <Input
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
          {type === 'number' && (
            <Input
              placeholder={placeholder}
              type="number"
              value={value}
              onChange={(e) => onChange(Number(e.target.value))}
            />
          )}
          {type === 'boolean' && (
            <Select
              value={value.toString()}
              onValueChange={(v) => onChange(v === 'true')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">True</SelectItem>
                <SelectItem value="false">False</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
};

interface HubSpotMappingFormProps {
  type?: 'new_lead' | 'new_event' | 'new_partial_lead';
  objectMapping: ObjectParamMapping[];
  onChange: (mapping: ObjectParamMapping[]) => void;
  hubspotProperties: HubSpotProperty[];
}

export const HubSpotMappingForm = ({
  type,
  objectMapping,
  onChange,
  hubspotProperties,
}: HubSpotMappingFormProps) => {
  const [parameterPathQuery, setParameterPathQuery] = useState('');

  const parameterPathOptions = useMemo(() => {
    return type === 'new_lead' || type === 'new_partial_lead'
      ? SUPERFUNNEL_LEAD_KEYS
      : SUPERFUNNEL_EVENT_KEYS;
  }, [type]);

  return (
    <div className="pt-2">
      {objectMapping.map((item, index) => (
        <div
          className="my-2 flex w-full flex-col gap-2 rounded-lg border border-gray-200 p-2"
          // eslint-disable-next-line react/no-array-index-key
          key={index}
        >
          <div className="flex w-full items-center gap-4">
            {/* HubSpot Property Autocomplete */}
            <div className="flex-1">
              <Autocomplete
                modal
                placeholder="HubSpot Property"
                selected={item.key}
                onChange={(key) => {
                  if (
                    key &&
                    objectMapping.some((m, i) => m.key === key && i !== index)
                  ) {
                    return; // Don't allow duplicate keys
                  }
                  onChange(
                    objectMapping.map((m, i) =>
                      i === index ? { ...m, key: key || '' } : m,
                    ),
                  );
                }}
                renderOption={(option) => {
                  const prop = hubspotProperties.find((p) => p.name === option);
                  if (!prop) {
                    return <>{option}</>;
                  }
                  return (
                    <>
                      <span>{prop.label}</span>
                      <span className="ms-2 text-xs text-gray-500">
                        {prop.name}
                      </span>
                    </>
                  );
                }}
                getValue={(name) => {
                  const prop = hubspotProperties.find((p) => p.name === name);
                  return prop ? `${prop.label} (${prop.name})` : name;
                }}
                options={hubspotProperties
                  .filter(
                    (prop) =>
                      !objectMapping.some(
                        (m, i) => m.key === prop.name && i !== index,
                      ),
                  )
                  .map((prop) => prop.name)}
              />
            </div>

            {/* Value Type Selector */}
            <div className="flex shrink-0 gap-2">
              <Select
                value={item.value.type}
                onValueChange={(value: 'custom' | 'param') =>
                  onChange(
                    objectMapping.map((m, i) =>
                      i === index
                        ? {
                            ...m,
                            value: {
                              type: value,
                              value: '',
                            },
                          }
                        : m,
                    ),
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Value Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="param">Parameter</SelectItem>
                  <SelectItem value="custom">Custom Value</SelectItem>
                </SelectContent>
              </Select>
              <Button
                color="negative"
                onClick={() =>
                  onChange(objectMapping.filter((_, i) => i !== index))
                }
              >
                Remove
              </Button>
            </div>
          </div>

          {/* Custom Value Input */}
          {item.value.type === 'custom' && (
            <TypedTextField
              value={item.value.value}
              onChange={(value) =>
                onChange(
                  objectMapping.map((m, i) =>
                    i === index
                      ? {
                          ...m,
                          value: {
                            type: 'custom',
                            value,
                          },
                        }
                      : m,
                  ),
                )
              }
            />
          )}

          {/* Parameter Path Selector */}
          {item.value.type === 'param' && (
            <>
              <Autocomplete
                modal
                placeholder="Parameter Path"
                selected={item.value.value}
                onInputChange={(value) => setParameterPathQuery(value)}
                renderOption={(option) => {
                  const field = parameterPathOptions.find(
                    (x) => x.key === option,
                  );

                  if (!field) {
                    return <>{option}</>;
                  }

                  return (
                    <>
                      <span>{field.key}</span>
                      <span className="ms-2 text-xs text-gray-600">
                        Example: {field.example}
                      </span>
                    </>
                  );
                }}
                onChange={(value) =>
                  onChange(
                    objectMapping.map((m, i) =>
                      i === index
                        ? {
                            ...m,
                            value: {
                              ...m.value,
                              type: 'param',
                              value: value || '',
                            },
                          }
                        : m,
                    ),
                  )
                }
                options={[
                  ...(parameterPathQuery ? [parameterPathQuery] : []),
                  ...parameterPathOptions.map((x) => x.key),
                ]}
              />

              {/* Default Value */}
              <div className="flex flex-col gap-1">
                <FieldTitle title="Default Value" />
                <TypedTextField
                  placeholder="Default Value"
                  value={(item.value as any).defaultValue}
                  onChange={(value) =>
                    onChange(
                      objectMapping.map((m, i) =>
                        i === index
                          ? {
                              ...m,
                              value: {
                                ...(m.value as any),
                                type: 'param',
                                defaultValue: value,
                              },
                            }
                          : m,
                      ),
                    )
                  }
                />
              </div>

              {/* Data Type */}
              <Select
                value={(item.value as any).dataType ?? 'none'}
                onValueChange={(value: 'none' | 'date') =>
                  onChange(
                    objectMapping.map((m, i) =>
                      i === index
                        ? {
                            ...m,
                            value: {
                              ...m.value,
                              type: 'param',
                              value: m.value.value,
                              dataType: value === 'none' ? null : 'date',
                            },
                          }
                        : m,
                    ),
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Data Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                </SelectContent>
              </Select>

              {/* Date Format Options */}
              {(item.value as any).dataType === 'date' && (
                <>
                  <Input
                    placeholder="Date Format"
                    value={(item.value as any).dateFormat ?? ''}
                    onChange={(e) =>
                      onChange(
                        objectMapping.map((m, i) =>
                          i === index
                            ? {
                                ...m,
                                value: {
                                  ...m.value,
                                  dateFormat: e.target.value,
                                },
                              }
                            : m,
                        ),
                      )
                    }
                  />
                  <Input
                    placeholder="Timezone"
                    value={(item.value as any).timezone ?? ''}
                    onChange={(e) =>
                      onChange(
                        objectMapping.map((m, i) =>
                          i === index
                            ? {
                                ...m,
                                value: {
                                  ...m.value,
                                  timezone: e.target.value,
                                },
                              }
                            : m,
                        ),
                      )
                    }
                  />
                </>
              )}
            </>
          )}
        </div>
      ))}

      {/* Add Field Button */}
      <div className="mt-2 flex w-full">
        <Button
          variant="outline"
          className="w-full"
          onClick={() =>
            onChange([
              ...objectMapping,
              {
                key: '',
                value: {
                  type: 'param',
                  value: '',
                },
              },
            ])
          }
        >
          Add Field
        </Button>
      </div>

      {objectMapping.length === 0 && (
        <div className="mt-2 rounded-lg border border-dashed border-gray-300 p-4 text-center text-gray-500">
          No field mappings configured. Click &quot;Add Field&quot; to create a
          mapping.
        </div>
      )}
    </div>
  );
};
